import { Injectable } from '@angular/core';
import { enumLengthUnit, enumWeightUnit } from '@data-layer/enums/common';
import { PackageModel, PackageUpdateModel } from '@data-layer/models/lcl';
import { ManagementDraftPackageModel, ManagementPackageModel } from '@data-layer/models/management';
import { precisionRound } from '@helpers/data-transform.helper';
import { FreightDimensionsModel } from '@data-layer/models/common';
import {
  CENTIMETER_METRE_RATIO,
  INCH_METRE_RATIO,
  KG_TO_TON_RATIO,
  LB_TO_KG_RATIO,
  MAX_NON_STACKABLE_HEIGHT_CENTIMETRE,
  MAX_NON_STACKABLE_HEIGHT_INCH,
  METRE_CENTIMETER_RATIO,
  OCEAN_DIM_FACTOR,
} from '@constants/constants';

@Injectable({ providedIn: 'root' })
export class LclContainerService {
  constructor() {}

  public calculateCBMOrCBF(
    qty: number,
    length: number,
    width: number,
    height: number,
    lengthUnit: enumLengthUnit,
    weight: number,
    weightUnit: enumWeightUnit,
    isStackAble: boolean,
  ): number {
    // see https://www.cogoport.com/blogs/what-is-cbm-and-how-do-you-calculate-it

    if (!length || !width || !height || !weight) {
      return 0;
    }

    const { cubicValue, weightValue }: { cubicValue: number; weightValue: number } = this.convertFormats(
      qty,
      length,
      width,
      height,
      lengthUnit,
      weight,
      weightUnit,
      isStackAble,
    );

    // check what is bigger: weight or length
    return qty * (cubicValue >= weightValue ? cubicValue : weightValue);
  }

  public calculateFullVolume(
    qty: number,
    length: number,
    width: number,
    height: number,
    lengthUnit: enumLengthUnit,
    weight: number,
    weightUnit: enumWeightUnit,
    isStackAble: boolean,
  ): number {
    if (!length || !width || !height || !weight) {
      return 0;
    }

    const { cubicValue }: { cubicValue: number } = this.convertFormats(
      qty,
      length,
      width,
      height,
      lengthUnit,
      weight,
      weightUnit,
      isStackAble,
    );

    return qty * cubicValue;
  }

  public convertPackagesDataForForm(
    packages: Array<PackageModel | ManagementPackageModel | ManagementDraftPackageModel>,
  ): Array<PackageModel | ManagementPackageModel | ManagementDraftPackageModel> {
    return packages.map((item: PackageModel | ManagementPackageModel | ManagementDraftPackageModel) => {
      const dimensions: FreightDimensionsModel = { ...item.dimensions };
      let lengthUnit: enumLengthUnit = item.lengthUnit;
      if (item.lengthUnit === enumLengthUnit.METER) {
        // convert M to CM (UI only)
        dimensions.length = precisionRound(dimensions.length * METRE_CENTIMETER_RATIO);
        dimensions.width = precisionRound(dimensions.width * METRE_CENTIMETER_RATIO);
        dimensions.height = precisionRound(dimensions.height * METRE_CENTIMETER_RATIO);
        lengthUnit = enumLengthUnit.CENTIMETER;
      }
      return { ...item, lengthUnit, dimensions };
    });
  }

  public convertPackagesDataForCreateSending(packages: PackageUpdateModel[], removeId?: boolean): PackageUpdateModel[] {
    return this.convertPackagesData(packages, removeId) as PackageUpdateModel[];
  }

  public convertPackagesDataForUpdateSending(packages: PackageModel[]): PackageModel[] {
    return this.convertPackagesData(packages, false) as PackageModel[];
  }

  public convertPackagesDataForManagementSending(
    packages: ManagementDraftPackageModel[],
    removeId?: boolean,
    addOriginalId?: boolean,
  ): ManagementDraftPackageModel[] {
    return this.convertPackagesData(packages, removeId, addOriginalId) as ManagementDraftPackageModel[];
  }

  public totalWeight(
    packagesData: Array<PackageModel | PackageUpdateModel | ManagementPackageModel | ManagementDraftPackageModel>,
  ): number {
    const roundVal: number = 100;

    return (
      Math.round(
        (packagesData || []).reduce(
          (acc: number, curr: PackageModel) =>
            acc + curr.quantity * Number(curr.weightUnit === enumWeightUnit.KG ? curr.weight : curr.weight / LB_TO_KG_RATIO),
          0,
        ) * roundVal,
      ) / roundVal
    );
  }

  public totalCBM(packagesData: Array<PackageModel | PackageUpdateModel | ManagementPackageModel | ManagementDraftPackageModel>): number {
    // #7015
    return precisionRound(
      (packagesData || []).reduce(
        (acc: number, curr: PackageModel) =>
          acc +
          this.calculateFullVolume(
            Number(curr.quantity),
            Number(curr.dimensions.length),
            Number(curr.dimensions.width),
            Number(curr.dimensions.height),
            curr.lengthUnit,
            Number(curr.weight),
            curr.weightUnit,
            curr.stackable,
          ),
        0,
      ),
    );
  }

  public totalUnits(packagesData: Array<PackageModel | PackageUpdateModel | ManagementPackageModel | ManagementDraftPackageModel>): number {
    return Math.round((packagesData || []).reduce((acc: number, curr: PackageModel) => acc + curr.quantity, 0));
  }

  public convertSidebarEditSingleData(
    packagesDataItems: PackageModel[],
    packageData: PackageModel,
    packageEdited: PackageModel[],
    id: number,
  ): PackageModel[] {
    return packagesDataItems.map((item: PackageModel, index: number) => {
      if (index === id) {
        return packageEdited[0];
      } else {
        return this.convertExistingPackagesData(packageData, packageEdited, item);
      }
    });
  }

  public convertExistingPackagesData(packageData: PackageModel, packageEdited: PackageModel[], item: PackageModel): PackageModel {
    if (
      (packageData.lengthUnit !== packageEdited[0]?.lengthUnit && packageEdited[0]?.lengthUnit !== enumLengthUnit.CENTIMETER) ||
      packageData.weightUnit !== packageEdited[0]?.weightUnit
    ) {
      let dimensions: FreightDimensionsModel = item.dimensions;
      if (packageEdited[0]?.lengthUnit === enumLengthUnit.INCH && item.lengthUnit === enumLengthUnit.METER) {
        dimensions = {
          length: precisionRound(Number(item.dimensions.length) * METRE_CENTIMETER_RATIO),
          width: precisionRound(Number(item.dimensions.width) * METRE_CENTIMETER_RATIO),
          height: precisionRound(Number(item.dimensions.height) * METRE_CENTIMETER_RATIO),
        };
      }
      return { ...item, lengthUnit: packageEdited[0]?.lengthUnit, weightUnit: packageEdited[0]?.weightUnit, dimensions };
    } else {
      return item;
    }
  }

  private convertPackagesData(
    packages: Array<PackageUpdateModel | PackageModel | ManagementDraftPackageModel>,
    removeId?: boolean,
    addOriginalId?: boolean,
  ): Array<PackageUpdateModel | PackageModel | ManagementDraftPackageModel> {
    return packages.map((p: PackageUpdateModel | PackageModel | ManagementDraftPackageModel) => {
      if (addOriginalId) {
        (p as ManagementDraftPackageModel).originalPackageId = p?.id;
      }

      if (removeId) {
        delete p.id;
      }

      if (p.lengthUnit === enumLengthUnit.CENTIMETER) {
        return {
          ...p,
          dimensions: {
            ...p.dimensions,
            length: precisionRound(Number(p.dimensions.length) * CENTIMETER_METRE_RATIO),
            width: precisionRound(Number(p.dimensions.width) * CENTIMETER_METRE_RATIO),
            height: precisionRound(Number(p.dimensions.height) * CENTIMETER_METRE_RATIO),
          },
          lengthUnit: enumLengthUnit.METER,
        };
      } else {
        if (removeId) {
          delete p.id;
        }

        return p;
      }
    });
  }

  private convertFormats(
    qty: number,
    length: number,
    width: number,
    height: number,
    lengthUnit: enumLengthUnit,
    weight: number,
    weightUnit: enumWeightUnit,
    isStackAble: boolean,
  ): { cubicValue: number; weightValue: number } {
    let cubicValue: number;
    let weightValue: number = weight / OCEAN_DIM_FACTOR;
    let fixedHeight: number = isStackAble ? height : MAX_NON_STACKABLE_HEIGHT_CENTIMETRE * CENTIMETER_METRE_RATIO;

    // Calculate CBM or CBF
    if (lengthUnit === enumLengthUnit.CENTIMETER) {
      fixedHeight = isStackAble ? height : MAX_NON_STACKABLE_HEIGHT_CENTIMETRE;
      cubicValue = length * CENTIMETER_METRE_RATIO * width * CENTIMETER_METRE_RATIO * fixedHeight * CENTIMETER_METRE_RATIO;
    } else if (lengthUnit === enumLengthUnit.INCH) {
      fixedHeight = isStackAble ? height : MAX_NON_STACKABLE_HEIGHT_INCH;
      cubicValue = length * INCH_METRE_RATIO * width * INCH_METRE_RATIO * fixedHeight * INCH_METRE_RATIO;
    } else {
      cubicValue = length * width * fixedHeight;
    }

    if (weightUnit === enumWeightUnit.LB) {
      weightValue = weightValue / LB_TO_KG_RATIO;
    }

    weightValue = weightValue * KG_TO_TON_RATIO;

    return { cubicValue, weightValue };
  }
}
